/**
 * pageMetrics.ts — Primitivas neutras de página para documentos (A4, mm ↔ px)
 *
 * Fonte única de verdade para dimensões de página e conversão de unidades,
 * compartilhada entre Contratos, Studio, geradores de PDF e pré-visualizações.
 *
 * Por que existe: antes, essas constantes viviam dentro de `contractStyles.ts`
 * (específico de contratos). Ao criar o Studio, copiar os valores geraria duas
 * fontes divergentes ("no preview está certo, no PDF está diferente"). Aqui elas
 * ficam neutras, sem qualquer regra de contrato.
 *
 * IMPORTANTE: navegadores modernos e o Chromium headless usado na geração de PDF
 * trabalham a 96 DPI. Portanto 1mm = 96/25.4 px ≈ 3.7795px nos dois contextos.
 *
 * RELEASE: v1.0.0 - 2026-09-09 - Extraído de contractStyles.ts (Studio, Fase 1)
 */

// =============================================================================
// DIMENSÕES A4 EM MILÍMETROS (unidade absoluta, independente de DPI)
// =============================================================================

/** Largura A4 em milímetros */
export const A4_WIDTH_MM = 210;

/** Altura A4 em milímetros */
export const A4_HEIGHT_MM = 297;

// =============================================================================
// CONVERSÃO DE UNIDADES
// =============================================================================

/** Fator de conversão mm → px a 96 DPI (≈ 3.7795) */
export const MM_TO_PX_96 = 96 / 25.4;

/**
 * Converte milímetros para pixels a 96 DPI (arredondado).
 * @param mm valor em milímetros
 * @returns valor equivalente em pixels
 */
export function mmToPx(mm: number): number {
  return Math.round(mm * MM_TO_PX_96);
}

/**
 * Converte pixels (96 DPI) para milímetros.
 * @param px valor em pixels
 * @returns valor equivalente em milímetros
 */
export function pxToMm(px: number): number {
  return px / MM_TO_PX_96;
}

// =============================================================================
// DIMENSÕES A4 EM PIXELS (96 DPI)
// =============================================================================

/** Largura A4 em pixels (96 DPI) — 210mm ≈ 794px */
export const A4_WIDTH_PX = mmToPx(A4_WIDTH_MM);

/** Altura A4 em pixels (96 DPI) — 297mm ≈ 1123px */
export const A4_HEIGHT_PX = mmToPx(A4_HEIGHT_MM);
